import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, bulkAddGmailLabel } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface BulkAddGmailLabelProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function BulkAddGmailLabelDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  changeLogRoles,
}: BulkAddGmailLabelProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [labelName, setLabelName] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isAdding } = useMutation({
    mutationFn: () =>
      bulkAddGmailLabel('User', selectedUsers, labelName, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message:
          'The task to add the gmail label to the selected user(s) has started.',
      });
      selectedUsers.forEach((user) => {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.GmailLabels, user],
        });
      });
      setChangeLogMessage('');
      setLabelName('');
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding the gmail label to the selected user(s). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_create_labels, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.BulkAddGmailLabel.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setLabelName('');
      }}
      title="Add Gmail Label"
      dialogContent={
        <>
          <Typography sx={{ mb: 2 }}>
            {selectedUsers.length > 1 ? (
              <>
                Add gmail label to <strong>{selectedUsers.length} users</strong>
                .
              </>
            ) : (
              <>
                Add gmail label to <strong>{selectedUsers[0]}</strong>.
              </>
            )}
          </Typography>
          <TextField
            autoFocus
            label="New Label Name"
            value={labelName}
            fullWidth
            onChange={(event) => {
              setLabelName(event.target.value);
            }}
          ></TextField>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isAdding}
          />
        </>
      }
      primaryButton={{
        loading: isAdding,
        action: handleSubmit,
        disabled: isAdding || labelName.length === 0,
        title: 'Add Label',
      }}
    />
  );
}
